/**
 * Copyright (c) VVDN Technologies Pvt Ltd. All rights reserved.
 * Proprietary and confidential — custom algorithms.
 */

import { channelsInterfere } from './rfChannels'
import { mountRfAdjustDb, defaultMountHeight } from './apMounts'
import { obstacleVerticalAttenuationDb } from './obstacles'
import { wallVerticalAttenuationDb } from './walls'
import { RF_GRID_COLS, RF_GRID_ROWS } from './rfGrid'
import type { FloorGeometry, HeatmapLayer, PlacedAp } from './types'

/** Typical client device height for vertical line-of-sight (m). */
const CLIENT_HEIGHT_M = 1.5

export type HeatmapCell = {
  col: number
  row: number
  x: number
  y: number
  rssi: number
  snr: number
  quality: number
  utilization: number
  interference: number
  clientDensity: number
  signal: number
  level: 'strong' | 'medium' | 'weak' | 'none'
}

function parseNum(v: unknown, def: number) {
  const n = Number(v)
  return Number.isFinite(n) ? n : def
}

function distanceM(x1: number, y1: number, x2: number, y2: number, widthM: number, heightM: number) {
  const dx = ((x2 - x1) / 100) * widthM
  const dy = ((y2 - y1) / 100) * heightM
  return Math.hypot(dx, dy)
}

/**
 * Indoor log-distance path loss (office environment).
 * PL(d) = PL0 + 10·n·log10(d), reference 1 m.
 * Exponent n ≈ 3.3 (2.4 GHz) / 3.5 (5 GHz) / 3.7 (6 GHz) — typical walled office.
 * Free-space FSPL understates loss and makes one AP cover unrealistically large floors.
 */
function pathLossDb(distM: number, bandGHz: number) {
  const d = Math.max(1, distM)
  const pl0 = bandGHz >= 6 ? 49 : bandGHz >= 5 ? 47 : 40
  const n = bandGHz >= 6 ? 3.7 : bandGHz >= 5 ? 3.5 : 3.3
  return pl0 + 10 * n * Math.log10(d)
}

function barrierLoss(
  ax: number,
  ay: number,
  px: number,
  py: number,
  apHeightM: number,
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
) {
  let loss = 0
  for (const w of geometry.walls) {
    loss += wallVerticalAttenuationDb(ax, ay, apHeightM, px, py, CLIENT_HEIGHT_M, w)
  }
  for (const o of geometry.obstacles) {
    loss += obstacleVerticalAttenuationDb(
      ax,
      ay,
      apHeightM,
      px,
      py,
      CLIENT_HEIGHT_M,
      o,
      floorWidthM,
      floorHeightM,
    )
  }
  return loss
}

function qualityFromRssi(rssi: number) {
  if (rssi >= -55) return 95
  if (rssi >= -65) return 80
  if (rssi >= -72) return 65
  if (rssi >= -80) return 45
  return 20
}

export type RfPointMetrics = {
  rssi: number
  snr: number
  quality: number
  utilization: number
  interference: number
  clientDensity: number
}

function apRssiAtPoint(
  ap: PlacedAp,
  px: number,
  py: number,
  geometry: FloorGeometry,
  widthM: number,
  heightM: number,
): number {
  const dist = distanceM(ap.x, ap.y, px, py, widthM, heightM)
  const tx = ap.txPowerDbm ?? 15
  const apH = ap.mountHeightM ?? defaultMountHeight(ap.mount)
  const loss = pathLossDb(dist, geometry.bandGHz) + barrierLoss(ap.x, ap.y, px, py, apH, geometry, widthM, heightM)
  const mountAdj = mountRfAdjustDb(ap, px, py, dist)
  return tx - loss + mountAdj
}

/** RSSI from a single AP at a floor point (serving-AP model for associated clients). */
export function rssiFromApAtPoint(
  ap: PlacedAp,
  px: number,
  py: number,
  geometry: FloorGeometry,
  widthM: number,
  heightM: number,
): number {
  return apRssiAtPoint(ap, px, py, geometry, widthM, heightM)
}

export type ApRfSignal = { rssi: number; channel?: number }

/**
 * Co-channel / adjacent-channel interference (0–100) from overlapping AP signals.
 */
export function coChannelInterferencePct(signals: ApRfSignal[], bandGHz = 5): number {
  if (signals.length <= 1) return 5

  const sorted = [...signals].sort((a, b) => b.rssi - a.rssi)
  const best = sorted[0]
  const second = sorted[1] ?? { rssi: -999, channel: undefined }
  const audible = sorted.filter(s => s.rssi > -82)
  if (audible.length <= 1) return 8

  const servingCh = best.channel
  const noiseFloorDb = -92
  let interfererLinear = 10 ** (noiseFloorDb / 10)
  for (let i = 1; i < audible.length; i++) {
    const sig = audible[i]
    if (channelsInterfere(servingCh, sig.channel, bandGHz)) {
      interfererLinear += 10 ** (sig.rssi / 10)
    }
  }
  const servingLinear = 10 ** (best.rssi / 10)
  const sinr = 10 * Math.log10(servingLinear / interfererLinear)

  let pct = 100 - (sinr + 5) * 3.2
  pct = Math.max(5, Math.min(100, pct))

  const delta = best.rssi - second.rssi
  if (
    second.rssi > -78
    && delta < 6
    && channelsInterfere(servingCh, second.channel, bandGHz)
  ) {
    pct = Math.max(pct, 55 + (6 - delta) * 5)
  }
  if (
    second.rssi > -72
    && delta < 3
    && channelsInterfere(servingCh, second.channel, bandGHz)
  ) {
    pct = Math.max(pct, 75 + (3 - delta) * 6)
  }

  // No co-channel energy beyond noise — cap (different-channel overlap is not co-channel).
  const hasCoChannelInterferer = audible.slice(1).some(sig =>
    channelsInterfere(servingCh, sig.channel, bandGHz),
  )
  if (!hasCoChannelInterferer) return Math.min(pct, 22)

  return pct
}

/** Per-point RF metrics for heatmap rendering (spatially varying). */
export function rfMetricsAtPoint(
  px: number,
  py: number,
  aps: PlacedAp[],
  geometry: FloorGeometry,
  widthM: number,
  heightM: number,
): RfPointMetrics {
  const apSignals: ApRfSignal[] = aps.map(ap => ({
    rssi: apRssiAtPoint(ap, px, py, geometry, widthM, heightM),
    channel: ap.channel,
  }))
  const bestRssi = apSignals.length ? Math.max(...apSignals.map(s => s.rssi)) : -95
  const interference = coChannelInterferencePct(apSignals, geometry.bandGHz)
  const rssi = aps.length ? bestRssi : -95
  const noiseFloor = -92 + ((px * 0.17 + py * 0.23) % 1) * 5 + Math.sin(px * 0.31) * 1.5
  const snr = rssi - noiseFloor
  const utilization = Math.min(
    100,
    aps.reduce((s, ap) => {
      const d = distanceM(ap.x, ap.y, px, py, widthM, heightM)
      return s + ((ap.clients ?? 0) + 1.5) * Math.exp(-d / 9) * 9
    }, 10),
  )
  const clientDensity = Math.min(
    100,
    aps.reduce((s, ap) => {
      const d = distanceM(ap.x, ap.y, px, py, widthM, heightM)
      return s + (ap.clients ?? 0) * Math.exp(-d / 11)
    }, 0) * 9,
  )
  return {
    rssi,
    snr,
    quality: qualityFromRssi(rssi),
    utilization,
    interference,
    clientDensity,
  }
}

export function formatRfMetric(layer: HeatmapLayer, m: RfPointMetrics): string {
  switch (layer) {
    case 'snr':
      return `${Math.round(m.snr)} dB SNR`
    case 'quality':
      return `${Math.round(m.quality)}% quality`
    case 'utilization':
      return `${Math.round(m.utilization)}% util`
    case 'interference':
      return `${Math.round(m.interference)}% interference`
    case 'clientDensity':
      return `${Math.round(m.clientDensity)}% clients`
    default:
      return `${Math.round(m.rssi)} dBm`
  }
}

function metric(layer: HeatmapLayer, rssi: number, snr: number, util: number, interference: number, density: number) {
  switch (layer) {
    case 'snr':
      return snr + 100
    case 'quality':
      return qualityFromRssi(rssi)
    case 'utilization':
      return util
    case 'interference':
      return interference
    case 'clientDensity':
      return density
    default:
      return rssi + 100
  }
}

function level(signal: number): HeatmapCell['level'] {
  if (signal >= 75) return 'strong'
  if (signal >= 50) return 'medium'
  if (signal >= 25) return 'weak'
  return 'none'
}

export function computeHeatmapGrid(
  aps: PlacedAp[],
  geometry: FloorGeometry,
  widthM: number,
  heightM: number,
  cols = RF_GRID_COLS,
  rows = RF_GRID_ROWS,
): HeatmapCell[] {
  const layer = geometry.heatmapLayer
  const grid: HeatmapCell[] = []
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = ((col + 0.5) * 100) / cols
      const y = ((row + 0.5) * 100) / rows
      const m = rfMetricsAtPoint(x, y, aps, geometry, widthM, heightM)
      const signal = metric(layer, m.rssi, m.snr, m.utilization, m.interference, m.clientDensity)
      grid.push({
        col,
        row,
        x,
        y,
        rssi: Math.round(m.rssi),
        snr: Math.round(m.snr),
        quality: Math.round(m.quality),
        utilization: Math.round(m.utilization),
        interference: Math.round(m.interference),
        clientDensity: Math.round(m.clientDensity),
        signal,
        level: level(signal),
      })
    }
  }
  return grid
}

export function rssiAtPoint(
  px: number,
  py: number,
  aps: PlacedAp[],
  geometry: FloorGeometry,
  widthM: number,
  heightM: number,
) {
  let best = -999
  for (const ap of aps) {
    best = Math.max(best, apRssiAtPoint(ap, px, py, geometry, widthM, heightM))
  }
  return aps.length ? best : -95
}

export function heatmapColor(layer: HeatmapLayer, cell: HeatmapCell, alphaScale = 1): string {
  const v = metric(
    layer,
    cell.rssi,
    cell.snr,
    cell.utilization,
    cell.interference,
    cell.clientDensity,
  )
  if (layer === 'rssi' || layer === 'snr' || layer === 'quality') {
    if (v >= 75) return `rgba(5,150,105,${0.55 * alphaScale})`
    if (v >= 55) return `rgba(217,119,6,${0.45 * alphaScale})`
    return `rgba(239,68,68,${0.45 * alphaScale})`
  }
  if (v >= 70) return `rgba(239,68,68,${0.5 * alphaScale})`
  if (v >= 45) return `rgba(245,158,11,${0.4 * alphaScale})`
  return `rgba(34,197,94,${0.35 * alphaScale})`
}
